/**
 * Credit buckets screen (DNKADM-0052 U2) — the `v2_credit_bucket` list +
 * create/edit/deactivate/reactivate (handoff §5.4). `RequireAdmin`-gated route + a
 * Configuration nav item.
 *
 * The credit bucket is the first-class object every credit-bearing feature keys off
 * by `creditBucketId` (§5.4). Loads `listCreditBuckets({ status })` through
 * `useResource`, keyed on the Active/Inactive/All filter (`useActiveFilter` +
 * `<ActiveFilter>`, §4.3). A table of name → contact-system field key → price per
 * credit → status with Edit + Deactivate/Reactivate. "Add bucket" and Edit open the
 * {@link CreditBucketDialog}. Every write refetches + toasts. A first-run empty
 * tenant (§8) shows a strong empty state with a create CTA.
 *
 * **Price per credit (DNKADM-0237).** The column renders the stored price in the
 * tenant currency (`useTenantCurrency` + `formatMoney` — never a hardcoded `$`), or
 * a neutral "Not sellable" marker when unpriced, so an operator can see at a glance
 * why a bucket does not appear in the member app's Buy-more catalog.
 *
 * **Unit (DNKADM-0319).** A narrow column shows each bucket's member-facing unit
 * (Hours / Sessions / Credits) so an operator can see at a glance which buckets
 * are still on the `credits` default — those render de-emphasized.
 *
 * White-label (§4.0): the `ghlCustomFieldKey` column is headed brand-neutrally via
 * `CONTACT_SYSTEM_FIELD_KEY_LABEL` and renders the raw key value (data, not a
 * brand).
 *
 * **Toolbar, sort and URL (DNKADM-0430 P1-F).** The shared {@link ListToolbar} holds a
 * name/field-key search and the Active/Inactive/All filter; every data column has a shared
 * {@link SortableHeader} (default Name ascending). Search, filter and sort live in the URL
 * (`creditBucketsListView.ts`).
 */
import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { ListNoMatch } from '@/components/list/ListNoMatch';
import { ListToolbar } from '@/components/list/ListToolbar';
import { SortableHeader } from '@/components/list/SortableHeader';
import { parseSort, sortRows, toggleSort, toSortParams } from '@/components/list/listSort';
import { useListUrlState } from '@/components/list/useListUrlState';
import { Icon, resolveTokens } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { useUrlActiveFilter } from '@/hooks/useActiveFilter';
import { useTenantCurrency } from '@/hooks/useTenantCurrency';
import { formatMoney } from '@/utils/v2Format';
import { ActiveFilter } from '@/components/ActiveFilter';
import { useToast } from '@/components/useToast';
import { serverMessage } from '@/api/serverMessage';
import { CONTACT_SYSTEM_FIELD_KEY_LABEL } from '@/utils/whitelabel';
import {
  DEFAULT_CREDIT_UNIT,
  creditUnitLabel,
  deactivateCreditBucket,
  listCreditBuckets,
  reactivateCreditBucket,
  type CreditBucket,
} from '@/api/creditBuckets';
import { CreditBucketDialog } from './CreditBucketDialog';
import {
  CREDIT_BUCKET_DEFAULT_SORT,
  CREDIT_BUCKET_SORT_KEYS,
  CREDIT_BUCKETS_URL_DEFAULTS,
  bucketName,
  creditBucketMatches,
  creditBucketSortValue,
  type CreditBucketSortKey,
} from './creditBucketsListView';
import { ScreenError } from '@/components/ScreenError';

export function CreditBucketsScreen() {
  const t = resolveTokens(useTheme());
  const showToast = useToast();
  // DNKADM-0430: search, status filter and sort come from the URL (`creditBucketsListView.ts`).
  const url = useListUrlState(CREDIT_BUCKETS_URL_DEFAULTS);
  const setUrl = url.set;
  const filter = useUrlActiveFilter(url.values.status, setUrl);
  const search = url.values.q;
  const sort = parseSort(
    url.values.sort,
    url.values.dir,
    CREDIT_BUCKET_SORT_KEYS,
    CREDIT_BUCKET_DEFAULT_SORT,
  );
  const { currency } = useTenantCurrency();

  const buckets = useResource(
    useCallback(() => listCreditBuckets({ status: filter.status }), [filter.status]),
    [filter.status],
  );

  // Dialog state: `null` = closed; `{ bucket: null }` = add; `{ bucket }` = edit.
  const [dialog, setDialog] = useState<{ bucket: CreditBucket | null } | null>(null);

  const refetch = buckets.refetch;

  const visibleRows = useMemo(
    () =>
      sortRows(
        (buckets.data ?? []).filter((bucket) => creditBucketMatches(bucket, search)),
        (bucket) => creditBucketSortValue(bucket, sort.key),
        sort.dir,
        (bucket) => bucket.id,
      ),
    [buckets.data, search, sort.key, sort.dir],
  );
  const onSort = useCallback(
    (key: CreditBucketSortKey) =>
      setUrl(toSortParams(toggleSort(sort, key), CREDIT_BUCKET_DEFAULT_SORT)),
    [setUrl, sort],
  );

  const handleDeactivate = useCallback(
    async (bucket: CreditBucket) => {
      try {
        await deactivateCreditBucket(bucket.id);
        refetch();
        showToast('Credit bucket deactivated', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t deactivate the credit bucket.', 'error');
      }
    },
    [refetch, showToast],
  );

  const handleReactivate = useCallback(
    async (bucket: CreditBucket) => {
      try {
        await reactivateCreditBucket(bucket.id);
        refetch();
        showToast('Credit bucket reactivated', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t reactivate the credit bucket.', 'error');
      }
    },
    [refetch, showToast],
  );

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          mb: 2,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6" component="h2">
            Credit buckets
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
            The credit pools services, caps, bands, and memberships all draw from.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Icon name="add" />}
          onClick={() => setDialog({ bucket: null })}
          sx={{ flexShrink: 0 }}
        >
          Add bucket
        </Button>
      </Stack>

      <ListToolbar
        search={{
          value: search,
          onChange: (next) => setUrl({ q: next }),
          placeholder: 'Search credit buckets',
          disabled: buckets.status !== 'ready',
        }}
        filters={
          <ActiveFilter
            value={filter.value}
            onChange={filter.setValue}
            aria-label="Filter credit buckets by status"
          />
        }
        onClear={() => setUrl({ q: null, status: null })}
        canClear={search.length > 0 || filter.value !== 'active'}
      />

      {buckets.status === 'loading' && (
        <Skeleton
          variant="rounded"
          height={220}
          role="status"
          aria-label="Loading credit buckets"
          sx={{ borderRadius: 3 }}
        />
      )}

      {buckets.status === 'error' && (
        <ScreenError
          error={buckets.error}
          what="the credit buckets"
          onRetry={buckets.refetch}
          sx={{ alignItems: 'center' }}
        />
      )}

      {buckets.status === 'ready' && buckets.data && buckets.data.length === 0 && (
        // First-run empty state (§8) with a strong create CTA.
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: t.lineStrong,
            borderRadius: 3,
          }}
        >
          <Icon name="account_balance_wallet" sx={{ fontSize: 40, color: 'text.disabled' }} />
          <Typography sx={{ mt: 1, fontWeight: 600 }}>
            {filter.value === 'active' ? 'No credit buckets yet' : 'No buckets match this filter'}
          </Typography>
          {filter.value === 'active' && (
            <>
              <Typography variant="body2" sx={{ mt: 0.5, mb: 2.5, color: 'text.secondary' }}>
                Add your first credit bucket — services, caps, and memberships all draw from one.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Icon name="add" />}
                onClick={() => setDialog({ bucket: null })}
              >
                Add bucket
              </Button>
            </>
          )}
        </Paper>
      )}

      {buckets.status === 'ready' && buckets.data && buckets.data.length > 0 && (
        <TableContainer>
          <Table aria-label="Credit buckets" size="small">
            <TableHead>
              <TableRow>
                <SortableHeader label="Name" col="name" sort={sort} onSort={onSort} />
                <SortableHeader
                  label={CONTACT_SYSTEM_FIELD_KEY_LABEL}
                  col="fieldKey"
                  sort={sort}
                  onSort={onSort}
                />
                <SortableHeader label="Price per credit" col="price" sort={sort} onSort={onSort} />
                <SortableHeader label="Unit" col="unit" sort={sort} onSort={onSort} />
                <SortableHeader label="Status" col="status" sort={sort} onSort={onSort} />
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((bucket) => (
                <TableRow key={bucket.id}>
                  <TableCell>{bucketName(bucket)}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    {bucket.ghlCustomFieldKey ?? '—'}
                  </TableCell>
                  <TableCell>
                    {bucket.pricePerCredit != null ? (
                      currency ? (
                        formatMoney(bucket.pricePerCredit, currency)
                      ) : (
                        bucket.pricePerCredit.toFixed(2)
                      )
                    ) : (
                      <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
                        Not sellable
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    sx={bucket.unit === DEFAULT_CREDIT_UNIT ? { color: 'text.secondary' } : undefined}
                  >
                    {creditUnitLabel(bucket.unit)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={bucket.isActive ? 'Active' : 'Inactive'}
                      color={bucket.isActive ? 'success' : 'default'}
                      variant={bucket.isActive ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconAction
                      size="small"
                      label={`Edit bucket ${bucket.name || bucket.id}`}
                      onClick={() => setDialog({ bucket })}
                    >
                      <Icon name="edit" />
                    </IconAction>
                    {bucket.isActive ? (
                      <IconAction
                        size="small"
                        label={`Deactivate bucket ${bucket.name || bucket.id}`}
                        onClick={() => void handleDeactivate(bucket)}
                      >
                        <Icon name="toggle_off" />
                      </IconAction>
                    ) : (
                      <IconAction
                        size="small"
                        label={`Reactivate bucket ${bucket.name || bucket.id}`}
                        onClick={() => void handleReactivate(bucket)}
                      >
                        <Icon name="toggle_on" />
                      </IconAction>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {buckets.status === 'ready' &&
        buckets.data &&
        buckets.data.length > 0 &&
        visibleRows.length === 0 && <ListNoMatch noun="credit buckets" query={search} />}

      <CreditBucketDialog
        open={dialog !== null}
        bucket={dialog?.bucket ?? null}
        currency={currency}
        onClose={() => setDialog(null)}
        onSaved={() => {
          setDialog(null);
          refetch();
        }}
        showToast={showToast}
      />
    </Box>
  );
}
